/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: GatewaysPage.tsx (Refactored)
 🗓️ Created: 2025-01-17
 🧩 Description:
    SIP Gateways management page with list view.
    Refactored to use modular hooks and components.

 🔧 Notes:
    - Uses DataGridSimple for list view with pagination
    - Supports CRUD operations with permission control
    - Integrates with telephony database
    - Uses domain-based relationships

 🔁 Last Updated: 2025-01-17 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { Globe } from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import { useNavigationStore } from '../../store/navigation';
import { useNotifications } from '../../contexts/NotificationContext';
import { useGatewayList } from './hooks/useGatewayList';
import { Gateway } from './types';
import { deleteGatewayById, navigateToGatewayDuplicate } from './gatewaysActions';
import { reloadGateway, stopGateway, fetchGatewayStatus } from './api/gatewayApi';
import {
  renderNameColumn,
  renderUsernameColumn,
  renderProxyColumn,
  renderRegisterColumn,
  renderEnabledColumn,
  renderDescriptionColumn,
  renderCreatedAtColumn,
  renderActionsColumn
} from './components/GatewayColumnRenderers';

export default function GatewaysPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setCurrentPageTitle } = useNavigationStore();
  const { showSuccess, showError } = useNotifications();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [gatewayToDelete, setGatewayToDelete] = useState<Gateway | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Real-time status map: gatewayId → state string (e.g. 'NOREG', 'REGED', 'UNKNOWN')
  const [gatewayStatuses, setGatewayStatuses] = useState<Record<number | string, string>>({});

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useGatewayList();

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  // Fetch real-time status for all gateways whenever list data changes
  useEffect(() => {
    if (!data?.length) return;
    const loadStatuses = async () => {
      const results = await Promise.allSettled(
        data.map(async (gw) => {
          const status = await fetchGatewayStatus(gw.uuid ?? gw.id);
          return { id: gw.id, state: status.state };
        })
      );
      const map: Record<number | string, string> = {};
      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          map[r.value.id] = r.value.state;
        }
      });
      setGatewayStatuses(map);
    };
    loadStatuses();
  }, [data]);

  const handleCreate = useCallback(() => {
    navigate('/pbx/calls-routing/gateways/new');
  }, [navigate]);

  const handleEdit = useCallback((gateway: Gateway) => {
    navigate(`/pbx/calls-routing/gateways/${gateway.uuid ?? gateway.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((gateway: Gateway) => {
    navigateToGatewayDuplicate(navigate, gateway.uuid ?? gateway.id);
  }, [navigate]);

  const handleDeleteClick = useCallback((gateway: Gateway) => {
    setGatewayToDelete(gateway);
    setShowDeleteModal(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!gatewayToDelete) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteGatewayById(gatewayToDelete.uuid ?? gatewayToDelete.id);
      showSuccess(t('gateways.deletedSuccessfully'));
      setShowDeleteModal(false);
      setGatewayToDelete(null);
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('gateways.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [gatewayToDelete, refresh, showError, showSuccess, t]);

  const handleReload = useCallback(async (gateway: Gateway) => {
    try {
      const result = await reloadGateway(gateway.uuid ?? gateway.id);
      showSuccess(result.message || t('gateways.reloadSuccess'));
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('gateways.reloadError');
      showError(message);
    }
  }, [refresh, showError, showSuccess, t]);

  const handleStop = useCallback(async (gateway: Gateway) => {
    try {
      const result = await stopGateway(gateway.uuid ?? gateway.id);
      showSuccess(result.message || t('gateways.stopSuccess'));
      setGatewayStatuses(prev => ({ ...prev, [gateway.id]: 'UNKNOWN' }));
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('gateways.stopError');
      showError(message);
    }
  }, [refresh, showError, showSuccess, t]);

  const handleStart = useCallback(async (gateway: Gateway) => {
    try {
      const result = await reloadGateway(gateway.uuid ?? gateway.id);
      showSuccess(result.message || t('gateways.startSuccess'));
      setGatewayStatuses(prev => ({ ...prev, [gateway.id]: 'NOREG' }));
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('gateways.startError');
      showError(message);
    }
  }, [refresh, showError, showSuccess, t]);

  const renderActions = useCallback((gateway: Gateway) => (
    renderActionsColumn(null, gateway, t, handleEdit, handleDuplicate, handleDeleteClick, handleReload, handleStop, gatewayStatuses[gateway.id] === 'UNKNOWN', handleStart)
  ), [handleDeleteClick, handleDuplicate, handleEdit, handleReload, handleStop, handleStart, gatewayStatuses, t]);

  const enhancedColumns = useMemo(() => {
    return columns.map((column) => {
      if (column.key === 'name') {
        return {
          ...column,
          render: (_value: unknown, item: Gateway) => renderNameColumn(_value, item, t)
        };
      }
      if (column.key === 'username') {
        return {
          ...column,
          render: (_value: unknown, item: Gateway) => renderUsernameColumn(_value, item)
        };
      }
      if (column.key === 'proxy') {
        return {
          ...column,
          render: (_value: unknown, item: Gateway) => renderProxyColumn(_value, item)
        };
      }
      if (column.key === 'register') {
        return {
          ...column,
          render: (_value: unknown, item: Gateway) => renderRegisterColumn(_value, item, t)
        };
      }
      if (column.key === 'enabled') {
        return {
          ...column,
          render: (_value: unknown, item: Gateway) => renderEnabledColumn(_value, item, t)
        };
      }
      if (column.key === 'description') {
        return {
          ...column,
          render: (_value: unknown, item: Gateway) => renderDescriptionColumn(_value, item)
        };
      }
      if (column.key === 'createdAt') {
        return {
          ...column,
          render: (_value: unknown, item: Gateway) => renderCreatedAtColumn(_value, item)
        };
      }
      if (column.key === 'actions') {
        return {
          ...column,
          render: (_value: unknown, item: Gateway) => renderActions(item)
        };
      }
      return column;
    });
  }, [columns, t, renderActions]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            data={data}
            titleIcon={<Globe className="h-5 w-5 text-primary" />}
            columns={enhancedColumns}
            renderActions={renderActions}
            onAdd={handleCreate}
            onDuplicate={handleDuplicate}
            title={t('gateways.title')}
            addButtonText={t('gateways.addGateway')}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            pagination={pagination}
            sortBy={sortBy}
            sortOrder={sortOrder}
            searchQuery={searchQuery}
            loading={loading}
            searchPlaceholder={t('gateways.search')}
            emptyMessage={t('gateways.noGateways')}
          />

          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleDeleteConfirm}
            title={t('gateways.deleteGateway')}
            message={t('gateways.confirmDelete', { name: gatewayToDelete?.name ?? '' })}
            confirmText={t('common.delete')}
            cancelText={t('common.cancel')}
            type="danger"
            isLoading={isDeleting}
          />
        </>
      }
      formView={null}
    />
  );
}
